import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import * as Tracking from '@/tracking.js'
import { postWithCsrfRetry } from '@/lib/http.js'

export const useCartStore = defineStore('cart', () => {
  const cartItems = ref([])
  const vouchers = ref([])
  const total = ref('0,00')
  const currency = ref('€')
  const currencyCode = ref('EUR')
  const services = ref({})
  const cartId = ref(null)
  const selectedService = ref(null)
  const servicePrice = ref(0)
  const inactivityTimer = ref(null)
  const backendSubtotalRef = ref(0)
  const cartData = ref(null)
  const lastCartFetchError = ref(null)
  
  const cartExpirationTimer = ref(null)
  const cartExpiresAt = ref(null)
  const reservationExpiresAt = ref(null)
  const paymentStarted = ref(false)
  let serverClockOffsetMs = 0

  const serverNow = () => Date.now() + serverClockOffsetMs

  const applyServerTime = (serverTime) => {
    const serverTimeMs = Date.parse(serverTime)
    serverClockOffsetMs = Number.isFinite(serverTimeMs) ? serverTimeMs - Date.now() : 0
  }

  const totalPrice = computed(() => total.value + ' €*')
  const totalQuantity = computed(() => cartItems.value.reduce((sum, item) => sum + (item.quantity || 1), 0))

  const findExistingCartData = () => {
    const keys = Object.keys(localStorage)
    const cartPattern = /^cartItems_(.+)$/
    
    for (const key of keys) {
      const cartMatch = key.match(cartPattern)
      if (cartMatch) {
        const foundCartId = cartMatch[1]
        const cartItemsStr = localStorage.getItem(key)
        if (cartItemsStr) {
          try {
            const items = JSON.parse(cartItemsStr)
            if (Array.isArray(items) && items.length > 0) {
              return foundCartId
            }
          } catch (e) {
          }
        }
      }
    }
    return null
  }

  const normalizeCartId = (id) => {
    if (id === null || id === undefined) return null
    const value = String(id).replace(/^"|"$/g, '').trim()
    if (!value || value === 'null' || value === 'undefined') return null
    return value
  }

  const getFallbackCartId = () => {
    return normalizeCartId(cartId.value) || normalizeCartId(localStorage.getItem('currentCartId'))
  }

  const getCartMeRequestConfig = () => {
    const fallbackCartId = getFallbackCartId()
    const config = { withCredentials: true }

    if (fallbackCartId) {
      config.headers = { 'X-Cart-ID': fallbackCartId }
      config.params = { cartId: fallbackCartId }
    }

    return config
  }

  const initializeStore = () => {
    const preLoginCartId = localStorage.getItem('preLoginCartId');
    if (preLoginCartId) {
      cartId.value = preLoginCartId;
      localStorage.setItem('currentCartId', preLoginCartId);
      localStorage.removeItem('preLoginCartId');
    } else {
      const savedCartId = localStorage.getItem('currentCartId');
      cartId.value = savedCartId;
    }
    const cartItemsKey = cartId.value ? `cartItems_${cartId.value}` : 'cartItems';
    const cartMetaKey = cartId.value ? `cartMeta_${cartId.value}` : 'cartMeta';
    const cartSubtotalKey = cartId.value ? `cartSubtotal_${cartId.value}` : 'cartSubtotal';
    const cartVouchersKey = cartId.value ? `cartVouchers_${cartId.value}` : 'cartVouchers';
    const savedServiceKey = cartId.value ? `selectedService_${cartId.value}` : 'selectedService';
    const savedCart = localStorage.getItem(cartItemsKey);
    const savedVouchers = localStorage.getItem(cartVouchersKey);
    const savedSubtotal = localStorage.getItem(cartSubtotalKey);
    const savedService = localStorage.getItem(savedServiceKey);
    const savedExpiresAt = localStorage.getItem('cartExpiresAt');

    if (savedExpiresAt) {
      const expiresAt = parseInt(savedExpiresAt);
      if (Date.now() > expiresAt) {
        fetchCart();
        return;
      } else {
        cartExpiresAt.value = expiresAt;
        startCartExpirationTimer(expiresAt - Date.now());
      }
    }
    
    if (savedCart) {
      try {
        cartItems.value = JSON.parse(savedCart) || [];
      } catch (e) {
        cartItems.value = [];
      }
    }
    
    if (savedVouchers) {
      try {
        vouchers.value = JSON.parse(savedVouchers) || [];
      } catch (e) {
        vouchers.value = [];
      }
    }
  
    backendSubtotalRef.value = savedSubtotal ? parseFloat(savedSubtotal) : 0
  
    if (savedService) {
      try {
        selectedService.value = JSON.parse(savedService);
        servicePrice.value = parseFloat(selectedService.value.price || 0);
      } catch (e) {
        selectedService.value = null;
        servicePrice.value = 0;
      }
    } else {
      selectedService.value = null;
      servicePrice.value = 0;
    }
    
    const cartMetaStr = localStorage.getItem(cartMetaKey);
    if (cartMetaStr) {
      try {
        const metaData = JSON.parse(cartMetaStr);
        total.value = metaData.total || '0,00';
        currency.value = metaData.currency || '€';
        currencyCode.value = metaData.currencyCode || 'EUR';
        services.value = metaData.services || {};
        selectedService.value = metaData.selectedService || selectedService.value;
        servicePrice.value = metaData.servicePrice || servicePrice.value;
      } catch (e) {
      }
    } else {
      total.value = backendSubtotalRef.value.toFixed(2).replace('.', ',');
    }
    
    if (cartItems.value.length > 0 && !cartExpiresAt.value) {
      setCartExpiration(1800);
    }
  }
  
  const setCartExpiration = (seconds = 1800) => {
    if (cartExpirationTimer.value) {
      clearTimeout(cartExpirationTimer.value)
      cartExpirationTimer.value = null
    }
    const expiresAt = Date.now() + (seconds * 1000)
    cartExpiresAt.value = expiresAt
    localStorage.setItem('cartExpiresAt', expiresAt.toString())
    startCartExpirationTimer(seconds * 1000)
  }
  
  const startCartExpirationTimer = (milliseconds) => {
    if (cartExpirationTimer.value) {
      clearTimeout(cartExpirationTimer.value)
    }
    
    cartExpirationTimer.value = setTimeout(() => {
      if (!paymentStarted.value) fetchCart()
    }, milliseconds)
  }
  
  const resetCartExpiration = () => {
    if (cartItems.value.length > 0) {
      setCartExpiration(1800) // Reset to 30 minutes
    } else {
      if (cartExpirationTimer.value) {
        clearTimeout(cartExpirationTimer.value)
        cartExpirationTimer.value = null
      }
      cartExpiresAt.value = null
      localStorage.removeItem('cartExpiresAt')
    }
  }
  
  const updateService = (service) => {
    selectedService.value = service
    servicePrice.value = service?.price || 0
    refreshTotal()
  }

  const setCartId = (id) => {
    if (!id) {
      cartId.value = null
      localStorage.removeItem('currentCartId')
      return
    }
    const cleanId = String(id).replace(/^"|"$/g, '')
    if (cartId.value && cartId.value !== cleanId) {
      migrateCartData(cartId.value, cleanId)
    }
    cartId.value = cleanId
    localStorage.setItem('currentCartId', cleanId)
  }
  
  const migrateCartData = (oldCartId, newCartId) => {
    const keysToMigrate = [
      'cartItems',
      'cartMeta', 
      'cartSubtotal',
      'cartVouchers',
      'selectedService',
      'servicePrice'
    ]
    
    keysToMigrate.forEach(key => {
      const oldKey = oldCartId ? `${key}_${oldCartId}` : key
      const newKey = `${key}_${newCartId}`
      
      const data = localStorage.getItem(oldKey)
      if (data) {
        localStorage.setItem(newKey, data)
      }
    })
  }

  const setCartItems = (items) => {
    cartItems.value = items
    resetInactivityTimer()
    refreshTotal()
    resetCartExpiration()
  }

  const setCartMeta = (metaData) => {
    const {
      subtotal: subTotal,
      currency: curr,
      currencyCode: currCode,
      services: serv,
      selectedService: selService,
      servicePrice: servPrice
    } = metaData

    backendSubtotalRef.value = parseFloat(subTotal ?? 0)
    currency.value = curr || "€"
    currencyCode.value = currCode || 'EUR'
    services.value = serv || {}
    selectedService.value = selService ?? null
    servicePrice.value = servPrice ?? 0

    // Save with cart ID
    const cartSubtotalKey = cartId.value ? `cartSubtotal_${cartId.value}` : 'cartSubtotal'
    localStorage.setItem(cartSubtotalKey, backendSubtotalRef.value.toString())

    total.value = backendSubtotalRef.value.toFixed(2).replace('.', ',')

    // Save meta with cart ID
    const cartMetaKey = cartId.value ? `cartMeta_${cartId.value}` : 'cartMeta'
    localStorage.setItem(cartMetaKey, JSON.stringify({
      total: total.value,
      currency: currency.value,
      currencyCode: currencyCode.value,
      services: services.value,
      selectedService: selectedService.value,
      servicePrice: servicePrice.value
    }))
  }

  // The browser cancels in-flight requests on navigation/unload. That is not an
  // application error, so it must not be reported as one (see #5306).
  const isAbortError = (error) =>
    axios.isCancel(error) || error?.code === 'ECONNABORTED' || error?.message === 'Request aborted'

  const clearCart = async ({ reason = 'manual' } = {}) => {
    resetCartState()
    refreshTotal()
    clearLocalStorage()
    if (cartExpirationTimer.value) {
      clearTimeout(cartExpirationTimer.value)
      cartExpirationTimer.value = null
    }
    cartExpiresAt.value = null
    localStorage.removeItem('cartExpiresAt')
  }

  // After login the backend decides which cart survives the association — a
  // merge can return a different id than the preserved guest cart. Follow the
  // backend id and drop the preLoginCartId marker so initializeStore() can no
  // longer resurrect the old id on the next reload.
  const adoptAssociatedCart = (backendCartId) => {
    localStorage.removeItem('preLoginCartId')
    if (backendCartId) {
      setCartId(String(backendCartId))
    }
  }

  // The preserved guest cart was already completed (checked out). Purge every
  // trace of it — including the preLoginCartId marker, which clearLocalStorage()
  // does not match — so a finished booking can't be rehydrated after the reload.
  const discardPreservedCart = async () => {
    localStorage.removeItem('preLoginCartId')
    await clearCart({ reason: 'cart_completed' })
  }

  const resetInactivityTimer = () => {
    if (inactivityTimer.value) clearTimeout(inactivityTimer.value)
    inactivityTimer.value = setTimeout(() => {
      if (!paymentStarted.value) fetchCart()
    }, 1800000) // 30 minutes
  }

  const rebuildServicesFromCartItems = (cartItemsValue, originalServices) => {
    const rebuilt = {};
    
    for (const [eventKey, event] of Object.entries(originalServices)) {
      const newEventItems = [];
      
      for (const item of event.items) {
        const ids = item.ids || [];
        const validIds = ids.filter(id => cartItemsValue.some(ci => ci.id == id));
        
        if (validIds.length > 0 || ids.length === 0) {
          const validSeats = item.seats ? item.seats.slice(0, validIds.length) : [];
          // Create new item reference
          newEventItems.push({ ...item, ids: [...validIds], seats: [...validSeats] });
        }
      }
      
      if (newEventItems.length > 0) {
        // Create new event reference
        rebuilt[eventKey] = { 
          ...event, 
          items: newEventItems,
          group: event.group ? { ...event.group } : null
        };
      }
    }
    
    // Return a new object
    return { ...rebuilt };
  };

  const refreshTotal = () => {
    const subtotal = backendSubtotalRef.value || 0;
    const service = parseFloat(servicePrice.value || 0);
    total.value = (subtotal + service).toFixed(2).replace('.', ',');
  };

  const deleteItemById = async (itemId, type = null) => {
    try {
      let removedTrackingItem = null
      let removedTrackingCurrency = currency.value || '€'
      let removedTrackingValue = 0

      const cartItemBeforeDelete = cartItems.value.find(ci => ci.id == itemId)
      if (cartItemBeforeDelete) {
        const quantity = Number(cartItemBeforeDelete.quantity || 1)
        const itemPrice = Number.parseFloat(String(cartItemBeforeDelete.price || 0).replace(',', '.')) || 0
        removedTrackingItem = {
          id: cartItemBeforeDelete.id,
          name: cartItemBeforeDelete.name || cartItemBeforeDelete.title || 'Ticket',
          date: cartItemBeforeDelete.date || '',
          price: itemPrice,
          currency: cartItemBeforeDelete.currency || removedTrackingCurrency,
          quantity
        }
        removedTrackingCurrency = removedTrackingItem.currency
        removedTrackingValue = itemPrice * quantity
      }

      await postWithCsrfRetry(route('cart.delete'), { items: [itemId] });
      
      const isVoucher = type === 'voucher' || 
                       services.value.voucher?.items?.some(v => v.id == itemId) ||
                       services.value.discount?.items?.some(v => v.id == itemId);
      
      if (isVoucher) {
        let removedPrice = 0;
        
        // Create new references for services to ensure reactivity
        if (services.value.voucher?.items) {
          const voucherToRemove = services.value.voucher.items.find(v => v.id == itemId);
          if (voucherToRemove) {
            removedPrice = parseFloat(voucherToRemove.price || 0);
            if (!removedTrackingItem) {
              const quantity = Number(voucherToRemove.quantity || 1)
              const totalPrice = Number.parseFloat(String(voucherToRemove.price || 0).replace(',', '.')) || 0
              const unitPrice = quantity > 0 ? totalPrice / quantity : totalPrice
              removedTrackingItem = {
                id: voucherToRemove.id,
                name: voucherToRemove.name || voucherToRemove.title || 'Voucher',
                date: '',
                price: unitPrice,
                currency: voucherToRemove.currency || removedTrackingCurrency,
                quantity
              }
              removedTrackingCurrency = removedTrackingItem.currency
              removedTrackingValue = totalPrice
            }
          }
          
          // Create a new array reference
          const filteredVoucherItems = services.value.voucher.items.filter(v => v.id != itemId);
          
          if (filteredVoucherItems.length > 0) {
            // Create a new object reference
            services.value = {
              ...services.value,
              voucher: {
                ...services.value.voucher,
                items: filteredVoucherItems
              }
            };
          } else {
            // Create a new services object without the voucher key
            const newServices = { ...services.value };
            delete newServices.voucher;
            services.value = newServices;
          }
        }
        
        if (services.value.discount?.items) {
          const discountToRemove = services.value.discount.items.find(v => v.id == itemId);
          if (discountToRemove) {
            removedPrice = parseFloat(discountToRemove.price || 0);
            if (!removedTrackingItem) {
              const quantity = Number(discountToRemove.quantity || 1)
              const totalPrice = Number.parseFloat(String(discountToRemove.price || 0).replace(',', '.')) || 0
              const unitPrice = quantity > 0 ? totalPrice / quantity : totalPrice
              removedTrackingItem = {
                id: discountToRemove.id,
                name: discountToRemove.name || discountToRemove.title || 'Discount',
                date: '',
                price: unitPrice,
                currency: discountToRemove.currency || removedTrackingCurrency,
                quantity
              }
              removedTrackingCurrency = removedTrackingItem.currency
              removedTrackingValue = totalPrice
            }
          }
          
          // Create a new array reference
          const filteredDiscountItems = services.value.discount.items.filter(v => v.id != itemId);
          
          if (filteredDiscountItems.length > 0) {
            services.value = {
              ...services.value,
              discount: {
                ...services.value.discount,
                items: filteredDiscountItems
              }
            };
          } else {
            const newServices = { ...services.value };
            delete newServices.discount;
            services.value = newServices;
          }
        }
        
        // Update vouchers array
        vouchers.value = vouchers.value.filter(v => v.id != itemId);
        
        if (removedPrice > 0) {
          backendSubtotalRef.value = Math.max(0, backendSubtotalRef.value - removedPrice);
        }
      } else {
        const item = cartItems.value.find(ci => ci.id == itemId);
        const removedPrice = item ? parseFloat(item.price || 0) * (item.quantity || 1) : 0;
        
        // Create new array reference for cartItems
        cartItems.value = cartItems.value.filter(ci => ci.id != itemId);
        
        // Ensure we're creating new references in rebuildServicesFromCartItems
        services.value = rebuildServicesFromCartItems(cartItems.value, services.value);
        
        backendSubtotalRef.value = Math.max(0, backendSubtotalRef.value - removedPrice);
      }

      if (removedTrackingItem && removedTrackingValue > 0) {
        const gaItems = Tracking.getCartItemsForGA([removedTrackingItem])
        Tracking.trackRemoveFromCart(removedTrackingCurrency, removedTrackingValue, gaItems)
      }
      
      refreshTotal();
      resetCartExpiration();
      
      if (backendSubtotalRef.value === 0 || (cartItems.value.length === 0 && vouchers.value.length === 0)) {
        resetCartState();
        window.location.href = '/';
        return true;
      }
      await fetchCartData();
      return true;
      
    } catch (error) {
      return false;
    }
    
  };

  const resetCartState = () => {
    cartItems.value = []
    vouchers.value = []
    backendSubtotalRef.value = 0
    total.value = '0,00'
    services.value = {}
    setCartId(null)
    resetService()
    if (cartExpirationTimer.value) {
      clearTimeout(cartExpirationTimer.value)
      cartExpirationTimer.value = null
    }
    cartExpiresAt.value = null
    reservationExpiresAt.value = null
    paymentStarted.value = false
  }

  const resetService = () => {
    selectedService.value = null
    servicePrice.value = 0
    const savedServiceKey = cartId.value ? `selectedService_${cartId.value}` : 'selectedService'
    localStorage.removeItem(savedServiceKey)
    localStorage.removeItem('selectedService')
    
    const servicePriceKey = cartId.value ? `servicePrice_${cartId.value}` : 'servicePrice'
    localStorage.removeItem(servicePriceKey)
    localStorage.removeItem('servicePrice')
  }

  const clearLocalStorage = () => {
    Object.keys(localStorage).forEach(key => {
      if (
        key.startsWith('cart') ||
        key.startsWith('selectedService') ||
        key.startsWith('servicePrice')
      ) {
        localStorage.removeItem(key)
      }
    })
  }

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(route('api.v2.cart.me'), getCartMeRequestConfig())

      updateCartData(data)
      refreshTotal()
      resetCartExpiration()
    } catch (error) {
      if (error?.response?.status === 404) {
        const fallbackCartId = getFallbackCartId()
        if (fallbackCartId) {
          try {
            const { data } = await axios.get(route('api.v2.cart.me'), {
              withCredentials: true,
              headers: { 'X-Cart-ID': fallbackCartId },
              params: { cartId: fallbackCartId }
            })

            updateCartData(data)
            return
          } catch (retryError) {
          }
        }

        await clearCart({ reason: 'not_found' })
      }
    }
  }

  const updateCartData = (data) => {
    if (data?.cart_id) {
      setCartId(data.cart_id)
    }

    if (data?.cart) {
      cartItems.value = flattenCartItems(data.cart)
    }
    
    if (data?.services) {
      services.value = data.services
    }
    
    if (data?.serviceitems) {
      // Store service items if needed
    }
    
    const hasSubtotal = data?.subtotal !== undefined && data?.subtotal !== null
    const hasTotal = data?.total !== undefined && data?.total !== null

    if (hasSubtotal || hasTotal) {
      const subtotalNumber = hasSubtotal ? parseFloat(data.subtotal) : parseFloat(data.total) || 0
      const totalNumber = hasTotal ? parseFloat(data.total) : subtotalNumber
      const detectedServicePrice = Math.max(totalNumber - subtotalNumber, 0)

      backendSubtotalRef.value = subtotalNumber
      servicePrice.value = detectedServicePrice
      total.value = totalNumber.toFixed(2).replace('.', ',')
    }
    
    if (data?.currency) {
      currency.value = data.currency
    }
    if (data?.currencyCode) {
      currencyCode.value = data.currencyCode
    }
    if (data && Object.prototype.hasOwnProperty.call(data, 'reservation_expires_at')) {
      reservationExpiresAt.value = data.reservation_expires_at || null
      applyServerTime(data.server_time)
    }
    paymentStarted.value = data?.payment_started === true
    cartData.value = data
    resetCartExpiration()
  }
  
  const flattenCartItems = (cartObject) => {
    const items = []
    Object.values(cartObject).forEach(group => {
      if (group.items) {
        items.push(...group.items.map(item => ({ ...item, eventName: group.group?.title })))
      }
    })
    return items
  }
  
  const describeCartFetchError = (error) => ({
    httpStatus: error?.response?.status ?? null,
    errorCode: error?.code ?? null,
    errorMessage: typeof error?.message === 'string' ? error.message : null,
    aborted: isAbortError(error),
  })

  const fetchCartData = async () => {
    try {
      const { data } = await axios.get(route('api.v2.cart.me'), getCartMeRequestConfig())
      lastCartFetchError.value = null
      updateCartData(data)
      return data
    } catch (error) {
      lastCartFetchError.value = describeCartFetchError(error)

      if (error?.response?.status === 404) {
        const fallbackCartId = getFallbackCartId()
        if (fallbackCartId) {
          try {
            const { data } = await axios.get(route('api.v2.cart.me'), {
              withCredentials: true,
              headers: { 'X-Cart-ID': fallbackCartId },
              params: { cartId: fallbackCartId }
            })

            lastCartFetchError.value = null
            updateCartData(data)
            return data
          } catch (retryError) {
            lastCartFetchError.value = describeCartFetchError(retryError)
          }
        }

        await clearCart({ reason: 'not_found' })
        return null
      }

      if (!isAbortError(error)) {
        window.mtErrorLogging?.log(error, {
          meta: {
            feature: 'cart',
            action: 'fetchCartData',
            cartId: cartId.value ?? null,
            ...lastCartFetchError.value,
          }
        })
      }

      return null
    }
  }

  const saveCartData = () => {
    if (!cartId.value) return
    
    const cartItemsKey = `cartItems_${cartId.value}`
    const cartMetaKey = `cartMeta_${cartId.value}`
    const cartSubtotalKey = `cartSubtotal_${cartId.value}`
    const cartVouchersKey = `cartVouchers_${cartId.value}`
    const savedServiceKey = `selectedService_${cartId.value}`
    const servicePriceKey = `servicePrice_${cartId.value}`
    
    localStorage.setItem(cartItemsKey, JSON.stringify(cartItems.value))
    localStorage.setItem(cartVouchersKey, JSON.stringify(vouchers.value))
    localStorage.setItem(cartSubtotalKey, backendSubtotalRef.value.toString())
    localStorage.setItem(cartMetaKey, JSON.stringify({
      total: total.value,
      currency: currency.value,
      currencyCode: currencyCode.value,
      services: services.value,
      selectedService: selectedService.value,
      servicePrice: servicePrice.value
    }))

    if (selectedService.value) {
      localStorage.setItem(savedServiceKey, JSON.stringify(selectedService.value))
    }
    localStorage.setItem(servicePriceKey, servicePrice.value.toString())
  }

  watch(cartItems, (newCart) => {
    saveCartData()
    resetInactivityTimer()
    refreshTotal()
    resetCartExpiration()
  }, { deep: true })

  watch(vouchers, (newVouchers) => {
    saveCartData()
    resetInactivityTimer()
    refreshTotal()
  }, { deep: true })

  watch([total, currency, currencyCode, services, selectedService, servicePrice],
    ([newTotal, newCurrency, newServices, newSelectedService, newServicePrice]) => {
      saveCartData()
    }, { deep: true }
  )

  watch(cartId, (newId, oldId) => {
    if (newId) {
      localStorage.setItem('currentCartId', newId)
      if (oldId && oldId !== newId) {
        const cartItemsKey = `cartItems_${newId}`
        const cartItemsStr = localStorage.getItem(cartItemsKey)
        
        if (cartItemsStr) {
          try {
            cartItems.value = JSON.parse(cartItemsStr) || []
          } catch (e) {
            cartItems.value = []
          }
        } else {
          cartItems.value = []
        }
        
        const cartMetaKey = `cartMeta_${newId}`
        const cartMetaStr = localStorage.getItem(cartMetaKey)
        if (cartMetaStr) {
          try {
            const meta = JSON.parse(cartMetaStr)
            total.value = meta.total || '0,00'
            currency.value = meta.currency || '€'
            currencyCode.value = meta.currencyCode || 'EUR'
            services.value = meta.services || {}
            selectedService.value = meta.selectedService || null
            servicePrice.value = meta.servicePrice || 0
          } catch (e) {
          }
        }
      }
    } else {
      localStorage.removeItem('currentCartId')
    }
  })

  initializeStore()

  return {
    cartItems,
    vouchers,
    total,
    currency,
    currencyCode,
    services,
    cartId,
    selectedService,
    servicePrice,
    totalPrice,
    totalQuantity,
    cartData,
    lastCartFetchError,
    cartExpiresAt,
    reservationExpiresAt,
    paymentStarted,
    serverNow,
    updateCartData,
    fetchCartData,
    flattenCartItems,
    fetchCart,
    setCartItems,
    setCartMeta,
    setCartId,
    clearCart,
    adoptAssociatedCart,
    discardPreservedCart,
    deleteItemById,
    rebuildServicesFromCartItems,
    refreshTotal,
    updateService,
    resetInactivityTimer,
    saveCartData, 
    findExistingCartData
  }
})
